import React from 'react';
import styled from 'styled-components';

import { ApiBalanceHistoryEvent } from '#/api/types';
import { useCopyToClipboard } from '#/components/basic/icons/CopyToClipboard';
import MaterialSymbol from '#/components/basic/icons/MaterialSymbol';
import RouterLink from '#/components/basic/Link/RouterLink';
import VaultBalanceEventSign from '#/components/vaults/components/VaultBalanceEventSign';
import {
  BANKROLL_BRIDGE_SOURCE,
  COMPETITION_REWARD_BRIDGE_SOURCE,
  BUILDER_REWARD_SOURCE,
  POINTS_REWARD_BRIDGE_SOURCE,
  REWARDS_BRIDGE_SOURCE,
  EXCHANGE_TRANSFER_BRIDGE_SOURCE_OR_TARGET,
  LOOPBACK_FROM_VAULT_TO_WALLET_BRIDGE_SOURCE,
} from '#/config/constants';
import { truncateAddressInTheMiddle } from '#/lib/string';
import { UnreachableCaseError } from '#/lib/utils';

import downDownIcon from './icons/downDown.svg';

const TypeWrap = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 8px;
`;

const IconWrap = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  flex-shrink: 0;
`;

const CopyableAddressWrap = styled.span<{ $copied: boolean }>`
  cursor: copy;
  color: ${({ $copied }) => ($copied ? 'var(--success)' : 'inherit')};
  &:hover {
    color: ${({ $copied }) => ($copied ? 'var(--success)' : 'var(--primary3)')};
    text-decoration: underline dotted;
    text-underline-offset: 2px;
  }
`;

function CopyableAddress({ address }: { address: string }) {
  const { onCopy, recentlyCopied } = useCopyToClipboard();
  return (
    <CopyableAddressWrap
      $copied={recentlyCopied}
      title={`${address} (Click to copy)`}
      onClick={event => {
        event.stopPropagation();
        onCopy(address);
      }}
    >
      {truncateAddressInTheMiddle(address)}
    </CopyableAddressWrap>
  );
}

/**
 * How the vault manager address is displayed next to vault events:
 * - `link` to the vault detail page (default)
 * - `text` e.g. inside clickable rows on mobile listing
 * - `hidden` e.g. on vault detail page where all events belong to the same vault
 */
export type VaultAddressType = 'link' | 'text' | 'hidden';

function VaultManagerAddress({
  address,
  display,
}: {
  address: string;
  display: VaultAddressType;
}) {
  switch (display) {
    case 'hidden':
      return null;
    case 'text':
      return (
        <>
          {' '}
          <span title={address}>{truncateAddressInTheMiddle(address)}</span>
        </>
      );
    case 'link':
      return (
        <>
          {' '}
          <RouterLink
            to={`/vaults/${address}/deposits-withdrawals/my`}
            title={address}
            // underline
            color="white"
          >
            {truncateAddressInTheMiddle(address)}
          </RouterLink>
        </>
      );
    default:
      throw new UnreachableCaseError(display);
  }
}

export default function BalanceTransactionType({
  transaction,
  vaultAddressType = 'link',
}: {
  transaction: ApiBalanceHistoryEvent;
  vaultAddressType?: VaultAddressType;
}) {
  const type = transaction.type;
  const vaultManager = transaction.vault?.manager;
  const isVault = !!vaultManager;
  switch (type) {
    case 'transferFromWallet':
      return (
        <TypeWrap>
          {transaction.bridgeSource ===
          EXCHANGE_TRANSFER_BRIDGE_SOURCE_OR_TARGET ? (
            <IconWrap>
              <MaterialSymbol icon="arrow_downward" color="var(--success)" />
            </IconWrap>
          ) : (
            <>
              <img src={downDownIcon} style={{ width: 16, margin: 4 }} />
            </>
          )}
          <span>
            Transfer from <CopyableAddress address={transaction.sourceWallet} />
          </span>
        </TypeWrap>
      );
    case 'deposit':
      if (
        transaction.bridgeSource === BANKROLL_BRIDGE_SOURCE ||
        transaction.bridgeSource === REWARDS_BRIDGE_SOURCE ||
        transaction.bridgeSource === POINTS_REWARD_BRIDGE_SOURCE ||
        transaction.bridgeSource === COMPETITION_REWARD_BRIDGE_SOURCE ||
        transaction.bridgeSource === BUILDER_REWARD_SOURCE
      ) {
        return (
          <TypeWrap>
            <img src={downDownIcon} style={{ width: 16, margin: 4 }} />
            {transaction.bridgeSource === BANKROLL_BRIDGE_SOURCE
              ? 'Bankroll'
              : transaction.bridgeSource === REWARDS_BRIDGE_SOURCE
              ? 'Referral Reward'
              : transaction.bridgeSource === POINTS_REWARD_BRIDGE_SOURCE
              ? 'Points Reward'
              : transaction.bridgeSource === BUILDER_REWARD_SOURCE
              ? 'Builder Reward'
              : transaction.bridgeSource === COMPETITION_REWARD_BRIDGE_SOURCE
              ? 'Competition Reward'
              : ''}
          </TypeWrap>
        );
      }
      // Withdrawal from a vault credited into the balance via loopback
      // (loopback deposits into a vault render as Vault Deposit below)
      if (
        transaction.bridgeSource === LOOPBACK_FROM_VAULT_TO_WALLET_BRIDGE_SOURCE
      ) {
        return (
          <TypeWrap>
            <IconWrap>
              <VaultBalanceEventSign type="withdrawal" />
            </IconWrap>
            Vault Withdrawal
          </TypeWrap>
        );
      }
    // Otherwise continue for standard Deposit & Pending one...
    // eslint-disable-next-line no-fallthrough
    case 'pendingDeposit':
      return (
        <TypeWrap>
          <IconWrap>
            {isVault ? (
              <VaultBalanceEventSign type="deposit" />
            ) : (
              <MaterialSymbol icon="arrow_downward" color="var(--success)" />
            )}
          </IconWrap>
          {vaultManager ? (
            <span>
              Vault Deposit
              <VaultManagerAddress
                address={vaultManager}
                display={vaultAddressType}
              />
            </span>
          ) : (
            'Deposit'
          )}
        </TypeWrap>
      );
    case 'transferToWallet':
      return (
        <TypeWrap>
          <IconWrap>
            <MaterialSymbol icon="arrow_upward" color="var(--main)" />
          </IconWrap>
          <span>
            Transfer to <CopyableAddress address={transaction.targetWallet} />
          </span>
        </TypeWrap>
      );
    case 'withdrawal':
      return (
        <TypeWrap>
          <IconWrap>
            {isVault ? (
              <VaultBalanceEventSign type="withdrawal" />
            ) : (
              <MaterialSymbol icon="arrow_upward" color="var(--main)" />
            )}
          </IconWrap>
          {isVault ? 'Vault Withdrawal' : 'Withdrawal'}
        </TypeWrap>
      );
    case 'unknown':
      return <>Unknown</>;
    default:
      throw new UnreachableCaseError(type);
  }
}
